import { ChevronDownIcon } from "lucide-react";
import React, { useEffect, useRef } from "react";
import { DateRange } from "react-day-picker";

import DatePickerWithRange from "../../DatePickerWithRange";
import FortnightPicker, { isCurrentFortnight } from "../../FortnightPicker";
import MultiSelect from "../../MultiSelect";
import { AsyncSingleSelect } from "../../async-select/async-single-select";
import DateInput from "../../custom-form-fields/DateInput";
import { FormSelectSingle } from "../../custom-form-fields/FormSelect";
import { FilterType } from "../CustomTable";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDataTableSlotContext } from "@/context/DataTableSlot";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useQueryFilterType } from "@/hooks/useQueryParams";
import { TAny, _TFloatingMenuCustomComponentProps } from "@/interfaces/globals";
import { cn } from "@/lib/utils";

type ITableFilters<T, P> = {
  filters: FilterType<T, P>;
  customFilter?: React.ReactNode;
  showFilters: boolean;
  filterOperation: useQueryFilterType;
  hide?: boolean;
};

const Filters = <T, P>({
  filters,
  customFilter,
  showFilters = true,
  filterOperation,
  hide,
}: ITableFilters<T, P>): React.JSX.Element => {
  const { setFilters } = useDataTableSlotContext<T, P>();
  const fortnightRef = useRef<{ reset: () => void } | null>(null); // this is when we reset filters
  const { resetSorting } = useDataTableSlotContext();
  const {
    keys,
    updateParams,
    clearParams,
    appendSameKeys,
    getAllValue,
    clearParam,
  } = filterOperation;
  const isLargeScreen = useMediaQuery("(max-width: 1455px)");
  const isMediumScreen = useMediaQuery("(max-width: 1407px)");
  const isSmallScreen = useMediaQuery("(max-width: 1238px)");

  const handleClearFilter = (): void => {
    resetSorting?.();
    clearParams();

    if (fortnightRef.current) {
      // this is to reset the fortnight filter value
      fortnightRef.current.reset();
    }
  };

  useEffect(() => {
    if (setFilters) {
      setFilters({
        customFilter,
        filters: filters as FilterType<T, P>,
        showFilters,
        filterOperation,
        handleClearFilter,
      });
    }
  }, [customFilter, filterOperation, filters, setFilters, showFilters]);

  if (hide) {
    return <></>;
  }

  const fortNightPickerFilter = (): JSX.Element => {
    return (
      <FortnightPicker
        key={"fortnight"}
        ref={fortnightRef}
        setDateRange={(range) => {
          clearParam("fortnight");
          appendSameKeys("fortnight", range?.at(0)?.toString() ?? "");
          appendSameKeys("fortnight", range?.at(-1)?.toString() ?? "");
        }}
      />
    );
  };

  const renderFilters = (
    filterEntries: [string, TAny][],
    side?: "top" | "right" | "bottom" | "left",
  ): (JSX.Element | null)[] => {
    return filterEntries.map(([key, value]) => {
      if (typeof value === "boolean") {
        if (key === "date") {
          return (
            <DateInput
              key={"date"}
              onChange={(date) => {
                if (date) {
                  updateParams("date", date?.toString());
                }
              }}
              className='w-fit dashed-border '
            />
          );
        } else if (key === "dateRange") {
          return (
            <DatePickerWithRange
              key={"dateRange"}
              side={side}
              className={cn(
                "[&>button]:w-fit dashed-border  h-9",
                side === "right" && "[&>button]:w-[180px]",
              )}
              defaultValue={keys?.dateRange as TAny}
              onChange={(date) => {
                const data = date as DateRange;
                clearParam("dateRange");
                if (data?.from && data?.to) {
                  appendSameKeys("dateRange", data?.from?.toDateString());
                  appendSameKeys("dateRange", data?.to?.toDateString());
                }
              }}
            />
          );
        }

        return null;
      }

      if (value.multiSelect) {
        return (
          <MultiSelect
            key={`${key}-multi-select`}
            side={side}
            label={value?.label || key}
            selectedItems={[]}
            setSelectedItems={(value) => {
              updateParams(key, value?.toString());
            }}
            values={value?.options?.map(
              (item: { id: string; label: string }) => ({
                key: item?.id,
                value: item?.label,
              }),
            )}
          />
        );
      }

      if (value?.isPaginatedSingleSelect) {
        return (
          <AsyncSingleSelect
            key={`${key}-single-select`}
            useQuery={value?.paginatedSelectConfig?.useQuery}
            label={value?.label || key}
            onChange={(value) => {
              updateParams(key, value);
            }}
            renderConfig={value?.paginatedSelectConfig?.renderConfig}
            baseParams={value?.paginatedSelectConfig?.baseQuery}
            side={side}
            value={keys ? keys[key]! : ""}
            placeholder={value?.label ? ` Select ${value.label}` : `${key} `}
            triggerClassName={cn(
              " hover:bg-secondary min-w-44 max-w-48  h-9",
              side !== "right" && " dashed-border",
            )}
          />
        );
      }

      return (
        <FormSelectSingle
          key={`${key}-single-select`}
          withoutForm
          side={side}
          field={{
            name: key,
            // eslint-disable-next-line no-empty-function
            onBlur: () => {},
            onChange: (v: string) => {
              if (v === "ALL") {
                updateParams(key, "");

                return;
              }
              updateParams(key, v);
              if (typeof filters[key] !== "boolean") {
                const item = filters[key] as unknown as {
                  linked: string;
                };

                if (item?.linked) {
                  updateParams(item?.linked, "");
                }
              }
            },
            // eslint-disable-next-line no-empty-function
            ref: () => {},
            value: keys ? keys[key] : "",
          }}
          options={value.options.map((item: { id: string; label: string }) => ({
            label: item.label,
            value: item?.id,
          }))}
          placeholder={value?.label ? ` ${value.label}` : `${key} `}
          className={cn(
            " hover:bg-secondary min-w-44 max-w-48  h-9",
            side !== "right" && " dashed-border",
          )}
          popoverClassName='max-w-[250px]'
        />
      );
    });
  };

  const filterEntries = Object?.entries(filters);
  const areAllValuesNull = (
    params: Record<string, string | null | undefined>,
  ): boolean =>
    params &&
    Object?.values(params)?.every((value) => value === null || value === "");

  const renderFortnightPicker = (): JSX.Element | null => {
    return Object?.keys(filters)?.includes("fortnight")
      ? fortNightPickerFilter()
      : null;
  };

  const renderDropdownMenu = (
    entries: typeof filterEntries,
    sliceStart: number = 0,
    sliceEnd?: number,
  ): React.ReactNode => {
    const slicedFilters = sliceEnd
      ? entries?.slice(sliceStart, sliceEnd)
      : entries?.slice(sliceStart);

    return slicedFilters?.length ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild className='h-9'>
          <Button
            variant='outline'
            className='px-2 font-normal text-gray-400 dashed-border'
          >
            More <ChevronDownIcon className='w-4 h-4 ml-2' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='start' className='space-y-2'>
          {renderFilters(slicedFilters, "right")}
        </DropdownMenuContent>
      </DropdownMenu>
    ) : null;
  };

  const getSliceCount = (
    isMediumScreen: boolean,
    isFortnightFilter: boolean,
  ): number => {
    if (isFortnightFilter) {
      return isMediumScreen ? 0 : 1;
    }

    return isMediumScreen ? 1 : 3;
  };

  const renderFiltersByScreenSize = (): React.ReactNode => {
    const isFortnightFilter = Object?.keys(filters)?.includes("fortnight");
    const filterCount = Object?.keys(filters)?.length;

    if (isSmallScreen) {
      if (isFortnightFilter) {
        return renderFortnightPicker();
      }
      if (filterCount) {
        return renderDropdownMenu(filterEntries) as React.ReactNode;
      }

      return null;
    }

    if ((isMediumScreen || isLargeScreen) && filterCount > 2) {
      const sliceCount = getSliceCount(isMediumScreen, isFortnightFilter);

      return (
        <div className='flex gap-2 '>
          {isFortnightFilter && renderFortnightPicker()}
          {renderFilters(filterEntries.slice(0, sliceCount))}
          {renderDropdownMenu(filterEntries, sliceCount) as React.ReactNode}
        </div>
      );
    }

    return (
      <div className='flex gap-2 '>
        {isFortnightFilter && renderFortnightPicker()}
        {renderFilters(filterEntries)}
      </div>
    );
  };

  const avoidToShowClearFilter = (
    params: Record<string, string | null | undefined>,
    keys: string[],
  ): boolean => {
    //Check Expected this keys does other keys has values then show clear filter
    const otherKeys =
      params && Object?.keys(params).filter((key) => !keys.includes(key));
    // Check if any other keys have non-empty values
    const hasOtherValues = otherKeys?.some((key) => {
      return (
        params[key] !== null && params[key] !== undefined && params[key] !== ""
      );
    });

    return hasOtherValues;
  };

  const showClearFortnight = (
    params: Record<string, string | null | undefined>,
  ): boolean => {
    // Filter out the 'fortnight' key to check other parameters
    const otherKeys = Object?.keys(params).filter(
      (key) => key !== "fortnight" && key !== "search",
    );

    // Check if any other keys have non-empty values
    const hasOtherValues = otherKeys?.some((key) => {
      return (
        params[key] !== null && params[key] !== undefined && params[key] !== ""
      );
    });

    // If any other keys have values, do not show the clear filter
    if (hasOtherValues) {
      return true;
    }

    // Extract fortnight value
    const fortnightKey = "fortnight";
    const fortnightValue = getAllValue(fortnightKey);

    // If 'fortnight' is empty, no need to show the clear filter
    if (!fortnightValue) {
      return false;
    }

    // Parse fortnight start and end dates
    const [startDate, endDate] = fortnightValue; // Assuming "fortnight" is a string with "start,end"
    if (!startDate || !endDate) {
      return false;
    }

    // Check if the fortnight is the current fortnight
    const isCurrent = isCurrentFortnight({
      start: new Date(startDate),
      end: new Date(endDate),
    });

    // Show clear filter only if fortnight is not the current fortnight
    return !isCurrent;
  };

  return (
    <div>
      {customFilter && customFilter}
      {showFilters && (
        <div className='flex items-center justify-between my-2'>
          {filterEntries.length > 0 && (
            <div className={cn("flex items-center gap-2")}>
              {renderFiltersByScreenSize()}
              {filterEntries?.length > 0 &&
                !areAllValuesNull(keys) &&
                avoidToShowClearFilter(keys, ["search", "orderBy", "sortBy"]) &&
                showClearFortnight(keys) && (
                  <Button
                    variant='ghost'
                    className='px-2 h-9'
                    onClick={() => handleClearFilter()}
                  >
                    Clear Filter
                  </Button>
                )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Filters;
